import { Controller } from "stimulus"
import { saveRegisteredEmail } from "./portal_registered_email"

export default class extends Controller {
  static targets = ["tab", "panel", "registerError", "registerSuccess", "loginEmail", "recaptcha"]
  static values = { fallbackRedirect: String }

  switchTab(event) {
    event.preventDefault()
    this.applyMode(event.currentTarget.dataset.mode)
  }

  submitRegister(event) {
    event.preventDefault()
    const form = event.currentTarget
    const email = form.querySelector('[name="email"]')?.value

    this.hideRegisterMessages()

    if (this.hasRecaptchaTarget && !form.querySelector('[name="g-recaptcha-response"]')?.value) {
      this.showRegisterError(this.recaptchaTarget.dataset.recaptchaErrorMessage)
      return
    }

    // Disabled while in flight: a double click would send a second register request,
    // which Portal answers with "email already exists" after the first one succeeded.
    const submitButton = form.querySelector('[type="submit"]')
    if (submitButton) submitButton.disabled = true

    fetch(form.action, {
      method: 'POST',
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      body: new FormData(form)
    })
      .then(res => res.json())
      .then(data => {
        this.resetRecaptcha()
        if (this.isRegisterSuccess(data)) {
          // Stays disabled: the page is navigating away.
          this.onRegisterSuccess(email, data)
        } else {
          if (submitButton) submitButton.disabled = false
          this.onRegisterError(data)
        }
      })
      .catch(() => {
        // No JSON to read: a 500 page, the HTML of a CSRF redirect, or a network failure.
        this.resetRecaptcha()
        if (submitButton) submitButton.disabled = false
        this.showRegisterError(this.fallbackErrorMessage())
      })
  }

  fallbackErrorMessage() {
    return this.hasRegisterErrorTarget ? this.registerErrorTarget.dataset.fallbackMessage : undefined
  }

  isRegisterSuccess(data) {
    return data.ok
  }

  // 註冊成功一律整頁跳轉，成功訊息在目的頁由 portal-register-notice 顯示。
  // fallback 路徑由 view 用 Rails route helper 帶進來，不在這裡寫死字串——路由改名時
  // JS 這側沒有任何測試守得住。
  onRegisterSuccess(email, data) {
    saveRegisteredEmail(email)
    window.location.assign(data.redirect_to || this.fallbackRedirectValue)
  }

  onRegisterError(data) {
    if (data.ok) return
    // Portal down: the popup carries the message, so the inline error would only repeat it.
    if (data.unavailable) {
      window.dispatchEvent(new CustomEvent('portal:unavailable'))
      return
    }
    this.showRegisterError(data.error)
  }

  hideRegisterMessages() {
    if (this.hasRegisterErrorTarget) this.registerErrorTarget.classList.add('hidden')
    if (this.hasRegisterSuccessTarget) this.registerSuccessTarget.classList.add('hidden')
  }

  resetRecaptcha() {
    if (!this.hasRecaptchaTarget || !window.grecaptcha) return
    const widgetDiv = this.recaptchaTarget.querySelector('[data-widget-id]')
    if (widgetDiv) {
      window.grecaptcha.reset(widgetDiv.dataset.widgetId)
    } else {
      window.grecaptcha.reset()
    }
  }

  clearRegisterError() {
    if (this.hasRegisterErrorTarget) this.registerErrorTarget.classList.add('hidden')
  }

  showRegisterError(message) {
    if (!this.hasRegisterErrorTarget) return
    this.registerErrorTarget.textContent = message
    this.registerErrorTarget.classList.remove('hidden')
  }

  applyMode(mode) {
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.mode === mode
      tab.classList.toggle("border-primary", active)
      tab.classList.toggle("text-white", active)
      tab.classList.toggle("border-transparent", !active)
      tab.classList.toggle("text-grey-5", !active)
    })
    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.mode !== mode)
    })
  }
}
